import { MeterType } from '@/api/energyConsumption';
import {
  DateType,
  formatValueAndSign,
  RankMethod,
  RankMethodDisplay,
  RankMethodType,
  xLabel,
} from '@/pages/EnergyAnalysisRanking/utils';
import { formatThousandUnit, keepPercentOrLine, keepTwoNull } from '@/utils/utils';
import { V2EnergyUnitConsumptionRankingPostResponse } from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import { CustomerRequest } from '../..';
// x轴

type ChartOptionProp = {
  rankMethod?: RankMethod;
  timeResolution?: DateType;
  energyMethod?: RankMethodType;
  selectedConsumption?: number;
  data?: V2EnergyUnitConsumptionRankingPostResponse['list'];
  dataQueryPeriodName?: string;
  customerRequest?: CustomerRequest;
};

export const getChartOption = ({
  rankMethod,
  timeResolution,
  energyMethod,
  selectedConsumption,
  data,
  dataQueryPeriodName,
  customerRequest,
}: ChartOptionProp) => {
  const sortData = data?.slice().reverse();
  let timeFormat: any;
  if (timeResolution === DateType.CUSTOM) {
    timeFormat =
      customerRequest?.timeResolution === DateType.WEEK
        ? `${xLabel(customerRequest?.timeResolution, customerRequest?.currentPeriod?.[0])} ~ ${xLabel(
            customerRequest?.timeResolution,
            customerRequest?.currentPeriod?.[1]
          )}`
        : `${dayjs(customerRequest?.currentPeriod?.[0]).format(xLabel(customerRequest?.timeResolution))} ~ ${dayjs(
            customerRequest?.currentPeriod?.[1]
          ).format(xLabel(customerRequest?.timeResolution))}`;
  } else {
    timeFormat = sortData?.[0]?.ts
      ? timeResolution === DateType.WEEK
        ? xLabel(timeResolution, sortData?.[0]?.ts)
        : dayjs(sortData?.[0]?.ts).format(xLabel(timeResolution))
      : '--';
  }

  const labelOption = (unit?: string, formTotal?: boolean) => ({
    show: true,
    position: 'right',
    fontSize: 12,
    padding: [2, 2, 2, 2],
    rich: {
      pos: {
        color: '#E64242', // 红色：上升
      },
      neg: {
        color: '#52C41A', // 绿色：下降
      },
    },
    formatter: (params: any) => {
      const { sign, total } = params.data;
      const value = formTotal ? total : params.value;
      const valueStr = unit === 'kWh' ? formatThousandUnit(Math.abs(value)) : keepTwoNull(Math.abs(value));
      if (rankMethod === RankMethod.BY_INCREMENT) {
        if (formTotal ? value > 0 : sign === '+') return `{pos|+${valueStr}${unit || 'kWh'}}`;
        if (formTotal ? value < 0 : sign === '-') return `{neg|-${valueStr}${unit || 'kWh'}}`;
      }
      return `${valueStr}${unit || 'kWh'}`;
    },
  });

  const series =
    selectedConsumption === MeterType.ELECTRICAL_ENERGY
      ? energyMethod === RankMethodType.ALL
        ? [
            {
              name: `尖时段-${RankMethodDisplay[rankMethod!]}`,
              type: 'bar',
              stack: 'total',
              data: sortData?.map(item => ({
                ...formatValueAndSign(item.summit!, item.summitIncrement, rankMethod!),
                id: item.energyUnitId,
                rate: item.summitRate,
              })),
              itemStyle: {
                color: '#FF6D2C', // 图形颜色
              },
            },
            {
              name: `峰时段-${RankMethodDisplay[rankMethod!]}`,
              type: 'bar',
              stack: 'total',
              data: sortData?.map(item => ({
                ...formatValueAndSign(item.peak!, item.peakIncrement, rankMethod!),
                id: item.energyUnitId,
                rate: item.peakRate,
              })),
              itemStyle: {
                color: '#CE90D1', // 图形颜色
              },
            },
            {
              name: `平时段-${RankMethodDisplay[rankMethod!]}`,
              type: 'bar',
              stack: 'total',
              data: sortData?.map(item => ({
                ...formatValueAndSign(item.plain!, item.plainIncrement, rankMethod!),
                id: item.energyUnitId,
                rate: item.plainRate,
              })),
              itemStyle: {
                color: '#0DB6D9', // 图形颜色
              },
            },
            {
              name: `谷时段-${RankMethodDisplay[rankMethod!]}`,
              type: 'bar',
              stack: 'total',
              label: labelOption(sortData?.[0]?.unit, true),
              data: sortData?.map(item => ({
                ...formatValueAndSign(item.valley!, item.valleyIncrement, rankMethod!),
                total: (rankMethod === RankMethod.BY_INCREMENT ? item.totalIncrement : item.total) || 0,
                id: item.energyUnitId,
                rate: item.valleyRate,
              })),
              itemStyle: {
                color: '#63BC7F', // 图形颜色
              },
            },
          ]
        : [
            energyMethod === RankMethodType.SHARP && {
              name: `尖时段-${RankMethodDisplay[rankMethod!]}`,
              type: 'bar',
              stack: 'total',
              label: labelOption(sortData?.[0]?.unit),
              data: sortData?.map(item => ({
                ...formatValueAndSign(item.summit!, item.summitIncrement, rankMethod!),
                id: item.energyUnitId,
              })),
              itemStyle: {
                color: '#FF6D2C', // 图形颜色
              },
            },
            energyMethod === RankMethodType.PEAK && {
              name: `峰时段-${RankMethodDisplay[rankMethod!]}`,
              type: 'bar',
              stack: 'total',
              label: labelOption(sortData?.[0]?.unit),
              data: sortData?.map(item => ({
                ...formatValueAndSign(item.peak!, item.peakIncrement, rankMethod!),
                id: item.energyUnitId,
              })),
              itemStyle: {
                color: '#CE90D1', // 图形颜色
              },
            },
            energyMethod === RankMethodType.AVERAGE && {
              name: `平时段-${RankMethodDisplay[rankMethod!]}`,
              type: 'bar',
              stack: 'total',
              label: labelOption(sortData?.[0]?.unit),
              data: sortData?.map(item => ({
                ...formatValueAndSign(item.plain!, item.plainIncrement, rankMethod!),
                id: item.energyUnitId,
              })),
              itemStyle: {
                color: '#0DB6D9', // 图形颜色
              },
            },
            energyMethod === RankMethodType.VALLEY && {
              name: `谷时段-${RankMethodDisplay[rankMethod!]}`,
              type: 'bar',
              stack: 'total',
              label: labelOption(sortData?.[0]?.unit),
              data: sortData?.map(item => ({
                ...formatValueAndSign(item.valley!, item.valleyIncrement, rankMethod!),
                id: item.energyUnitId,
              })),
              itemStyle: {
                color: '#63BC7F', // 图形颜色
              },
            },
          ]
      : [
          {
            name: `总${RankMethodDisplay[rankMethod!]}`,
            type: 'bar',
            stack: 'total',
            label: labelOption(sortData?.[0]?.unit),
            data: sortData?.map(item => ({
              ...formatValueAndSign(item.total!, item.totalIncrement, rankMethod!),
              id: item.energyUnitId,
            })),
          },
        ];

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: any[]) => {
        const unit = sortData?.[0]?.unit || '';
        const name = params[0]?.name || '';
        const total = sortData?.find(i => i.energyUnitId === params?.[0]?.data?.id)?.total;
        const totalIncrement = sortData?.find(i => i.energyUnitId === params?.[0]?.data?.id)?.totalIncrement;
        const { value, sign } = formatValueAndSign(total!, totalIncrement, rankMethod!);
        const showRate =
          selectedConsumption === MeterType.ELECTRICAL_ENERGY &&
          energyMethod === RankMethodType.ALL &&
          rankMethod !== RankMethod.BY_INCREMENT;
        let html = `<div>${timeFormat}&nbsp;&nbsp${name}</div>${
          energyMethod === RankMethodType.ALL
            ? `
            <div>
              ${dataQueryPeriodName ? dataQueryPeriodName + '-' : ''}总${RankMethodDisplay[rankMethod!]}：
              <span  style="color:${sign ? (sign === '+' ? '#E64242' : '#52C41A') : ''}">${sign || ''}${
                unit === 'kWh' ? formatThousandUnit(value) : keepTwoNull(value)
              }${unit}</span>
            </div>
          `
            : ``
        }`;

        params.forEach(p => {
          html += `<div>${p.marker}${dataQueryPeriodName ? dataQueryPeriodName + '-' : ''}${p.seriesName}：
          <span style="color:${p.data.sign ? (p.data.sign === '+' ? '#E64242' : '#52C41A') : ''}">${p.data.sign || ''}${
            unit === 'kWh' ? formatThousandUnit(p?.value) : keepTwoNull(p?.value)
          }${unit} </span> ${
            showRate
              ? `<div style="display: inline-block; margin-left: 12px">占比：${keepPercentOrLine(p.data?.rate)}%</div>`
              : ''
          }</div>`;
        });
        return html;
      },
    },
    legend: {
      show: energyMethod === RankMethodType.ALL && selectedConsumption === MeterType.ELECTRICAL_ENERGY,
      right: 20,
      itemGap: 18,
      itemWidth: 16,
      itemHeight: 7,
    },
    grid: {
      left: 20,
      right: 80,
      bottom: 30,
      top: 40,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      name: sortData?.[0]?.unit || 'kWh',
      splitLine: {
        show: true,
        lineStyle: {
          color: 'rgba(255,255,255,0.3)',
        },
      },
      nameTextStyle: { color: 'rgba(255,255,255,0.85)' },
      axisLine: {
        show: true,
        lineStyle: {
          type: 'solid',
          width: 1,
        },
      },
    },
    yAxis: {
      type: 'category',
      data: sortData?.map(item => item.energyUnitName),
      splitLine: {
        show: false,
        lineStyle: {
          type: 'dashed',
        },
      },
      axisTick: {
        show: true,
        lineStyle: {
          type: 'solid',
        },
      },
      axisLine: {
        show: true,
        lineStyle: {
          type: 'solid',
        },
      },
    },
    series: series,
  };
};
